import { Role, RoleAssignment } from '@adornis/abac/policies/SimpleRolePolicy.js';
import { logger } from '@adornis/base/logging.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { SmartTeamsAppUserGroup } from '../db/SmartTeamsAppUserGroup.js';
import { upsertSmartTeamsAppUser } from './upsert-app-user.js';

/**
 * Ensures a SmartTeamsAppUser record and Field Engineer role assignment exist for the
 * given userId. This allows FEs created in the backend to log into the smartteams-app
 * frontend.
 *
 * Idempotent — safe to call multiple times for the same userId. The shared
 * `upsertSmartTeamsAppUser` helper enforces the userGroup-match guard, so this throws
 * if the user is already onboarded as a Customer / Admin / PartnerManager.
 */
export async function ensureAppUserForFieldEngineer(params: {
  userId: string;
  email: string;
  username?: string;
  primaryLanguage?: string;
  serviceNowFEId?: string;
  companyId?: string | null;
}): Promise<void> {
  const { userId, email, username, primaryLanguage, serviceNowFEId, companyId } = params;

  await upsertSmartTeamsAppUser({
    userId,
    userGroup: SmartTeamsAppUserGroup.FieldEngineer,
    email,
    username: username ?? email,
    primaryLanguage,
    serviceNowFEId,
    companyId,
  });
  logger.info({ userId, email }, 'Upserted SmartTeamsAppUser for field engineer');

  // Ensure the Field Engineer role exists and is assigned to this user.
  const roleCol = await getRawCollection<Role>(Role._collectionName);
  let feRole = await roleCol.findOne({ roleName: 'Field Engineer' });
  if (!feRole) {
    // Create a minimal role — the app's ensureSmartTeamsAppRoles will augment permissions.
    feRole = new Role({ roleName: 'Field Engineer', permissions: [] });
    await feRole.create();
    logger.info('Created placeholder Field Engineer role');
  }

  const roleAssignmentCol = await getRawCollection<RoleAssignment>(RoleAssignment._collectionName);
  const existing = await roleAssignmentCol.findOne({ userId });
  if (!existing) {
    await new RoleAssignment({ userId, roleIds: [feRole._id] }).create();
  } else if (!existing.roleIds?.includes(feRole._id)) {
    await roleAssignmentCol.updateOne({ _id: existing._id }, { $addToSet: { roleIds: feRole._id } });
  }
}
